import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'

import { api, type Capabilities, type JobOrder } from '../api/client'
import ConfirmOrderDialog from '../components/ConfirmOrderDialog'
import NumberStepper from '../components/NumberStepper'
import { ALLOWED_EXTENSIONS } from '../lib/audioFile'
import { useBatch, type BatchItem } from '../order/useBatch'
import { useJobsContext } from '../queue/useJobsContext'
import { useWorkInFlight } from '../version/useWorkInFlight'

/**
 * Our default is no separation at all -- lighter and faster than the VM's own
 * default, and the common case here is covering a track as it stands. Anyone
 * who wants stems opens More options.
 */
/**
 * Cover strength: a 0.05 grid from 0 to 1, starting at 0.8.
 *
 * The GPU's own default is 0.96 and its contract accepts anything "zero or
 * higher", so this is deliberately narrower and lower than the VM would allow:
 * a product decision about what these covers should sound like, not a limit
 * coming from the API.
 */
// 0.15 is the VM's floor, not a preference. Below it the model collapses to
// noise and the job COMPLETES, returning a completely unrelated song with
// working download links -- nothing in the response tells it apart from a real
// cover, so it must never be offered.
const STRENGTH_MIN = 0.15
const STRENGTH_MAX = 1
const STRENGTH_STEP = 0.05
const STRENGTH_DEFAULT = 0.8
/** Four characters and two buttons; anything wider is just stretched. */
const STRENGTH_WIDTH = 220

/**
 * Cover variance, 0-100 each.
 *
 * A cover follows its source closely, which can leave it near enough to the
 * original recording to read as a clone. These alter the source before the model
 * sees it, so the difference carries into every cover made from it. The VM
 * defaults both to 0; these are ours, and they mean every order asks for
 * variance unless someone turns it down.
 *
 * tone_change is the expensive one: it forces a four-stem separation pass, about
 * a minute per song, even when no separation was asked for. estimate.ts counts
 * that.
 */
const VARIANCE_MAX = 100
const TONE_CHANGE_DEFAULT = 35
const TIMING_DRIFT_DEFAULT = 50
const SLIDER_WIDTH = 360

/**
 * infer_steps is deliberately not exposed. The GPU defaults it to 10 and nobody
 * ordering covers here has a reason to change it, so the field was one more
 * decision on a screen that should be about picking songs. Omitting it from the
 * request is what makes the VM apply its own default.
 */

const DEFAULT_ENGINE = 'none'

const ACCEPT = ALLOWED_EXTENSIONS.map((extension) => `.${extension}`).join(',')

function statusChip(item: BatchItem) {
  switch (item.status) {
    case 'checking':
      return <Chip size="small" label="Checking" />
    case 'rejected':
      return <Chip size="small" color="error" variant="outlined" label="Not accepted" />
    case 'queued':
      return <Chip size="small" label="Waiting" />
    case 'uploading':
      return <Chip size="small" color="info" label={`${Math.round(item.progress * 100)}%`} />
    case 'uploaded':
      return <Chip size="small" color="success" variant="outlined" label="Ready" />
    case 'ordering':
      return <Chip size="small" color="info" label="Ordering" />
    case 'ordered':
      return <Chip size="small" color="success" label="Ordered" />
    case 'failed':
      return <Chip size="small" color="error" label="Failed" />
  }
}

export default function OrderPage() {
  const batch = useBatch()

  // Tell the shell not to offer a reload while chunks are still going up:
  // they live in this tab, so a reload abandons them with nothing to resume.
  const { setBusy } = useWorkInFlight()
  const { refreshNow } = useJobsContext()
  useEffect(() => {
    setBusy(batch.uploading || batch.ordering)
    return () => setBusy(false)
  }, [batch.uploading, batch.ordering, setBusy])
  const [capabilities, setCapabilities] = useState<Capabilities | null>(null)
  const [engine, setEngine] = useState(DEFAULT_ENGINE)
  const [targets, setTargets] = useState<string[]>([])
  const [models, setModels] = useState<string[]>([])
  const [coverStrength, setCoverStrength] = useState(STRENGTH_DEFAULT)
  const [toneChange, setToneChange] = useState(TONE_CHANGE_DEFAULT)
  const [timingDrift, setTimingDrift] = useState(TIMING_DRIFT_DEFAULT)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    // Ask the VM what it can run rather than hardcoding. Failing is fine --
    // it is usually just powered off, and the defaults still work.
    api
      .gpuCapabilities()
      .then(setCapabilities)
      .catch(() => setCapabilities(null))
  }, [])

  const options: JobOrder = {
    separation_engine: engine,
    ...(targets.length > 0 ? { targets } : {}),
    ...(models.length > 0 ? { dit_models: models } : {}),
    ...(Number.isFinite(coverStrength) ? { cover_strength: coverStrength } : {}),
    // Sent even at 0, which is the VM's own default: an explicit value is what
    // makes the params echoed back match what the screen showed.
    tone_change: toneChange,
    timing_drift: timingDrift,
  }

  const selected = capabilities?.separation_engines.find((e) => e.name === engine)
  const effectiveTargets =
    targets.length > 0 ? targets : (selected?.default_targets ?? ['original'])
  const effectiveModels = models.length > 0 ? models : (capabilities?.default_models ?? ['', ''])

  const ready = batch.items.filter((item) => item.status === 'uploaded')
  const canOrder = !batch.uploading && !batch.ordering && ready.length > 0

  const pick = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return
      void batch.addFiles(Array.from(files))
      if (inputRef.current) inputRef.current.value = ''
    },
    [batch],
  )

  return (
    <Stack spacing={3}>
      <Typography variant="h5" component="h2">
        Order Covers
      </Typography>

      <Paper
        sx={{
          p: 4,
          borderStyle: 'dashed',
          borderWidth: 2,
          borderColor: 'divider',
          textAlign: 'center',
        }}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault()
          pick(event.dataTransfer.files)
        }}
      >
        <Stack spacing={1.5} alignItems="center">
          <CloudUploadIcon color="primary" sx={{ fontSize: 40 }} />
          <Typography>Drop audio files here, or choose them</Typography>
          <Typography variant="caption" color="text.secondary">
            {ALLOWED_EXTENSIONS.join(', ')} · up to 200 MB each · one cover order per file
          </Typography>
          <Button variant="outlined" component="label" disabled={batch.uploading}>
            Choose files
            <input
              ref={inputRef}
              hidden
              type="file"
              multiple
              accept={ACCEPT}
              onChange={(event) => pick(event.target.files)}
            />
          </Button>
        </Stack>
      </Paper>

      {batch.stopped && (
        <Alert severity="error" onClose={() => undefined}>
          {batch.stopped}
        </Alert>
      )}

      {batch.items.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            {batch.items.map((item) => (
              <Box key={item.id}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  {item.status === 'ordered' && (
                    <CheckCircleIcon color="success" fontSize="small" />
                  )}
                  {(item.status === 'failed' || item.status === 'rejected') && (
                    <ErrorOutlineIcon color="error" fontSize="small" />
                  )}
                  <Typography sx={{ flexGrow: 1, minWidth: 0 }} noWrap title={item.file.name}>
                    {item.file.name}
                  </Typography>
                  {statusChip(item)}
                  {!batch.uploading && !batch.ordering && item.status !== 'ordered' && (
                    <Button size="small" onClick={() => batch.remove(item.id)}>
                      Remove
                    </Button>
                  )}
                </Stack>
                {item.status === 'uploading' && (
                  <LinearProgress
                    variant="determinate"
                    value={item.progress * 100}
                    sx={{ mt: 0.5 }}
                  />
                )}
                {item.reason && (
                  <Typography variant="caption" color="error">
                    {item.reason}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>More options</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="caption" color="text.secondary">
              These apply to every file in the batch. Anything left alone uses the GPU&apos;s own
              default.
            </Typography>

            <FormControl fullWidth size="small">
              <InputLabel id="engine-label">Separation</InputLabel>
              <Select
                labelId="engine-label"
                label="Separation"
                value={engine}
                onChange={(event) => {
                  setEngine(event.target.value)
                  setTargets([])
                }}
              >
                {(capabilities?.separation_engines ?? [{ name: DEFAULT_ENGINE }]).map((option) => (
                  <MenuItem key={option.name} value={option.name}>
                    {option.name === 'none' ? 'None — cover the track as it is' : option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selected && selected.allowed_targets.length > 1 && (
              <FormControl fullWidth size="small">
                <InputLabel id="targets-label">Parts to cover</InputLabel>
                <Select
                  labelId="targets-label"
                  multiple
                  value={targets}
                  input={<OutlinedInput label="Parts to cover" />}
                  onChange={(event) =>
                    setTargets(
                      typeof event.target.value === 'string'
                        ? event.target.value.split(',')
                        : event.target.value,
                    )
                  }
                  renderValue={(value) => value.join(', ') || 'Default'}
                >
                  {selected.allowed_targets.map((target) => (
                    <MenuItem key={target} value={target}>
                      {target}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {capabilities && (
              <FormControl fullWidth size="small">
                <InputLabel id="models-label">Models</InputLabel>
                <Select
                  labelId="models-label"
                  multiple
                  value={models}
                  input={<OutlinedInput label="Models" />}
                  onChange={(event) =>
                    setModels(
                      typeof event.target.value === 'string'
                        ? event.target.value.split(',')
                        : event.target.value,
                    )
                  }
                  renderValue={(value) => value.join(', ') || 'Default'}
                >
                  {capabilities.allowed_models.map((model) => (
                    <MenuItem key={model} value={model}>
                      {model}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Stack spacing={1}>
              <Tooltip
                describeChild
                placement="top-start"
                title="tone_change: EQ across separated stems. Which frequencies move depends on the song."
              >
                {/* Inline and self-aligned, or the label fills the panel and the
                    tooltip anchors to the middle of that -- which is how it ended
                    up floating half a screen away from the words it describes. */}
                <Typography
                  variant="body2"
                  component="span"
                  id="tone-change-label"
                  sx={{ alignSelf: 'flex-start' }}
                >
                  How different it sounds
                </Typography>
              </Tooltip>
              <Slider
                value={toneChange}
                onChange={(_event: Event, value: number | number[]) =>
                  setToneChange(value as number)
                }
                min={0}
                max={VARIANCE_MAX}
                step={5}
                valueLabelDisplay="auto"
                aria-labelledby="tone-change-label"
                sx={{ maxWidth: SLIDER_WIDTH }}
              />
            </Stack>

            <Stack spacing={1}>
              <Tooltip
                describeChild
                placement="top-start"
                title="timing_drift: the speed wanders while the pitch and total length stay put."
              >
                <Typography
                  variant="body2"
                  component="span"
                  id="timing-drift-label"
                  sx={{ alignSelf: 'flex-start' }}
                >
                  How much the timing wanders
                </Typography>
              </Tooltip>
              <Slider
                value={timingDrift}
                onChange={(_event: Event, value: number | number[]) =>
                  setTimingDrift(value as number)
                }
                min={0}
                max={VARIANCE_MAX}
                step={5}
                valueLabelDisplay="auto"
                aria-labelledby="timing-drift-label"
                sx={{ maxWidth: SLIDER_WIDTH }}
              />
            </Stack>

            <NumberStepper
              label="Cover strength"
              value={coverStrength}
              onChange={setCoverStrength}
              min={STRENGTH_MIN}
              max={STRENGTH_MAX}
              step={STRENGTH_STEP}
              maxWidth={STRENGTH_WIDTH}
              helperText={`${STRENGTH_MIN.toFixed(2)} to ${STRENGTH_MAX.toFixed(2)}, in steps of ${STRENGTH_STEP}`}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant="contained"
          size="large"
          disabled={!canOrder}
          onClick={() => setConfirmOpen(true)}
        >
          {ready.length > 0
            ? `Order ${ready.length} Cover${ready.length === 1 ? '' : 's'}`
            : 'Order'}
        </Button>
        {batch.uploading && (
          <>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Uploading…
            </Typography>
            <Button size="small" onClick={batch.cancelUploads}>
              Cancel
            </Button>
          </>
        )}
        {batch.items.length > 0 && !batch.uploading && !batch.ordering && (
          <Button size="small" onClick={batch.reset}>
            Clear
          </Button>
        )}
      </Stack>

      <ConfirmOrderDialog
        open={confirmOpen}
        files={ready}
        targetCount={effectiveTargets.length}
        modelCount={effectiveModels.length}
        separates={engine !== 'none'}
        tonal={toneChange > 0}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={async () => {
          setConfirmOpen(false)
          await batch.order(options)
          // The queue is polled on a timer, but a submission is exactly the
          // event that should not wait for it: without this the sidebar badge
          // stayed empty for up to thirty seconds after ordering, which reads as
          // the order not having registered.
          await refreshNow()
        }}
      />
    </Stack>
  )
}
